import { useEffect, useMemo, useState } from 'react';
import {
  ARPEGGIO_QUALITIES,
  type ArpeggioQuality,
  type ArpeggioSelection,
  arpeggioLabel,
} from '@theory/arpeggios';
import { QUALITY_DISPLAY } from '@theory/chords';
import { SCALES, type ScaleId } from '@theory/scales';
import { CHROMATIC_SHARP } from '@theory/notes';
import shared from './dropdownShared.module.css';
import styles from './ArpeggioSheet.module.css';

interface ArpeggioSheetProps {
  open: boolean;
  selection: ArpeggioSelection;
  scaleRootNote: string | null;
  scaleId: ScaleId | null;
  onSelect: (next: ArpeggioSelection) => void;
  onClose: () => void;
}

export function ArpeggioSheet({
  open,
  selection,
  scaleRootNote,
  scaleId,
  onSelect,
  onClose,
}: ArpeggioSheetProps) {
  const initialMode = selection?.mode ?? 'diatonic';
  const initialSize = selection?.mode === 'diatonic' ? selection.size : 'triad';
  const initialFreeRoot = selection?.mode === 'free' ? selection.root : (scaleRootNote ?? 'C');
  const initialFreeQuality = selection?.mode === 'free' ? selection.quality : 'maj';

  const [mode, setMode] = useState<'diatonic' | 'free'>(initialMode);
  const [size, setSize] = useState<'triad' | 'seventh'>(initialSize);
  const [freeRoot, setFreeRoot] = useState<string>(initialFreeRoot);
  const [freeQuality, setFreeQuality] = useState<ArpeggioQuality>(initialFreeQuality);

  // Re-sync local state from the current selection each time the sheet opens,
  // mirroring the mobile sheet's useEffect-on-visible behaviour.
  useEffect(() => {
    if (open) {
      setMode(selection?.mode ?? 'diatonic');
      setSize(selection?.mode === 'diatonic' ? selection.size : 'triad');
      setFreeRoot(selection?.mode === 'free' ? selection.root : (scaleRootNote ?? 'C'));
      setFreeQuality(selection?.mode === 'free' ? selection.quality : 'maj');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scaleLength = useMemo(
    () => (scaleId ? SCALES[scaleId]?.intervals.length ?? 0 : 0),
    [scaleId],
  );

  const diatonicLabels = useMemo(() => {
    const labels: string[] = [];
    for (let degree = 1; degree <= scaleLength; degree++) {
      labels.push(arpeggioLabel({ mode: 'diatonic', degree, size }, scaleRootNote, scaleId));
    }
    return labels;
  }, [scaleLength, size, scaleRootNote, scaleId]);

  const activeDegree = selection?.mode === 'diatonic' ? selection.degree : null;
  const activeFreeQuality = selection?.mode === 'free' ? selection.quality : null;

  if (!open) return null;

  const handleDiatonicPick = (degree: number) => {
    onSelect({ mode: 'diatonic', degree, size });
  };
  const handleFreePick = (quality: ArpeggioQuality) => {
    setFreeQuality(quality);
    onSelect({ mode: 'free', root: freeRoot, quality });
  };
  const handleFreeRootChange = (root: string) => {
    setFreeRoot(root);
    if (selection?.mode === 'free') {
      onSelect({ mode: 'free', root, quality: freeQuality });
    }
  };
  const handleClear = () => {
    onSelect(null);
    onClose();
  };

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Arpeggios"
      onClick={onClose}
    >
      <div className={styles.sheet} data-scroll-surface onClick={(e) => e.stopPropagation()}>
        <div className={styles.titleRow}>
          <span className={styles.title}>Arpeggios</span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close arpeggio picker"
          >
            ×
          </button>
        </div>

        <div className={styles.seg} role="tablist" aria-label="Arpeggio mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'diatonic'}
            className={[styles.segOpt, mode === 'diatonic' && styles.active].filter(Boolean).join(' ')}
            onClick={() => setMode('diatonic')}
          >
            Diatonic
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'free'}
            className={[styles.segOpt, mode === 'free' && styles.active].filter(Boolean).join(' ')}
            onClick={() => setMode('free')}
          >
            Free-pick
          </button>
        </div>

        {mode === 'diatonic' && (
          <>
            <div className={styles.seg} role="tablist" aria-label="Arpeggio size">
              <button
                type="button"
                role="tab"
                aria-selected={size === 'triad'}
                className={[styles.segOpt, size === 'triad' && styles.active].filter(Boolean).join(' ')}
                onClick={() => setSize('triad')}
              >
                Triads
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={size === 'seventh'}
                className={[styles.segOpt, size === 'seventh' && styles.active].filter(Boolean).join(' ')}
                onClick={() => setSize('seventh')}
              >
                7ths
              </button>
            </div>

            <div className={styles.chipWrap}>
              {diatonicLabels.map((label, idx) => {
                const degree = idx + 1;
                const isActive = activeDegree === degree;
                return (
                  <button
                    type="button"
                    key={`dia-${degree}`}
                    className={[styles.chip, isActive && styles.active].filter(Boolean).join(' ')}
                    onClick={() => handleDiatonicPick(degree)}
                    aria-pressed={isActive}
                    aria-label={`Diatonic degree ${degree}: ${label}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {mode === 'free' && (
          <>
            <div className={styles.freeRootRow}>
              <span className={styles.freeRootLabel}>Root:</span>
              <select
                className={shared.select}
                value={freeRoot}
                onChange={(e) => handleFreeRootChange(e.target.value)}
                aria-label="Arpeggio root"
              >
                {CHROMATIC_SHARP.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.chipWrap}>
              {ARPEGGIO_QUALITIES.map((q) => {
                const display = `${freeRoot}${QUALITY_DISPLAY[q]}`;
                const isActive =
                  activeFreeQuality === q && selection?.mode === 'free' && selection.root === freeRoot;
                return (
                  <button
                    type="button"
                    key={`free-${q}`}
                    className={[styles.chip, isActive && styles.active].filter(Boolean).join(' ')}
                    onClick={() => handleFreePick(q)}
                    aria-pressed={isActive}
                    aria-label={`${display} arpeggio`}
                  >
                    {display}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {selection !== null && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClear}
            aria-label="Clear arpeggio overlay"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
